import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popup } from 'antd-mobile';
import * as echarts from 'echarts';

const indicatorFullNames = [
    'Energy Efficiency',
    'Equipment Condition',
    'Occupant Comfort',
    'Service Efficiency',
    'Technology Adoption',
    'Cost Efficiency'
];

const getAbbreviation = (name) => {
    return name.split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .join('');
};

const metricIndicators = indicatorFullNames.map((fullName) => ({
    name: getAbbreviation(fullName),
    fullName,
    max: 10
}));

const SpiderChart = ({ 
    radarData = [], 
    facilities = [], // 新增 facilities 参数来获取颜色信息
    title = "Portfolio Performance Overview", 
    showTitle = false,
    showOverallScore = false,
    overallScore = null,
    useMobileDrawerTooltip = false,
    style = { width: '100%', height: '400px' } 
}) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedRadarItem, setSelectedRadarItem] = useState(null);
    const drawerRadarItem = selectedRadarItem || radarData[0];
    const drawerMetrics = useMemo(() => {
        const values = drawerRadarItem?.radarData || [];
        return metricIndicators.map((indicator, index) => ({
            ...indicator,
            value: values[index]
        }));
    }, [drawerRadarItem]);

    // 初始化雷达图
    const initRadarChart = (retryCount = 0) => {
        const chartDom = chartRef.current;
        if (!chartDom) {
            if (retryCount < 5) {
                setTimeout(() => initRadarChart(retryCount + 1), 500);
            }
            return;
        }

        if (!radarData.length) {
            console.log('No radar data');
            return;
        }
        
        // 检查容器尺寸
        if (chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
            setTimeout(() => initRadarChart(), 500);
            return;
        }
        
        // 清除之前的图表实例
        if (chartInstanceRef.current) {
            chartInstanceRef.current.dispose();
        }
        
        const myChart = echarts.init(chartDom);
        chartInstanceRef.current = myChart;
        
        // 创建 facility 颜色映射
        const facilityColorMap = {};
        facilities.forEach(facility => {
            facilityColorMap[facility.id] = facility.color;
        });
        
        // 创建名称到缩写的映射函数
        const getAbbreviation = (name) => {
            // 将名称按空格分割，取每个单词的首字母并转为大写
            return name.split(' ')
                .map(word => word.charAt(0).toUpperCase())
                .join('');
        };
        
        // 定义指标全称列表
        const indicatorFullNames = [
            'Energy Efficiency',
            'Equipment Condition',
            'Occupant Comfort',
            'Service Efficiency',
            'Technology Adoption',
            'Cost Efficiency'
        ];
        
        // 创建名称映射表，用于 tooltip 显示全称
        const nameMap = {};
        const indicators = indicatorFullNames.map((fullName) => {
            const abbrev = getAbbreviation(fullName);
            nameMap[abbrev] = fullName;
            return {
                name: abbrev, // 使用缩写作为显示名称
                max: 10
            };
        });
        
        const hasOverallScore = overallScore !== null && overallScore !== undefined && !Number.isNaN(Number(overallScore));
        const mobileFacilityScore = !!useMobileDrawerTooltip;

        const option = {
            backgroundColor: '#fff',
            graphic: showOverallScore && hasOverallScore ? {
                type: 'text',
                left: 'center',
                top: '2%',
                style: {
                    text: `Overall Score: ${Number(overallScore).toFixed(2)}`,
                    fill: '#4b5563',
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: 'center'
                }
            } : undefined,
            title: showTitle ? {
                text: title,
                left: 'center',
                top: showOverallScore && hasOverallScore ? '10%' : '2%',
                textStyle: {
                    color: '#396598',
                    fontSize: 14
                }
            } : undefined,
            tooltip: mobileFacilityScore ? { show: false } : {
                trigger: 'item',
                confine: false, // 允许tooltip超出canvas边界
                enterable: true, // 允许鼠标进入tooltip
                appendToBody: true, // 将tooltip添加到body，避免被容器限制
                extraCssText: 'z-index: 9999;', // 设置更高的z-index
                className: 'custom-tooltip',
                formatter: function(params) {
                    // 对于雷达图，params.value 是一个数组，包含所有指标的值
                    if (Array.isArray(params.value)) {
                        // params.value 是值数组，需要与 indicators 对应
                        let result = `<strong>${params.name}</strong><br/>`;
                        return result + params.value.map((value, index) => {
                            // 通过索引找到对应的指标名称
                            const indicatorName = indicators[index]?.name || '';
                            const fullName = nameMap[indicatorName] || indicatorFullNames[index] || indicatorName;
                            // 显示格式：全称(缩写): 值
                            return `${fullName}(${indicatorName}): ${value}`;
                        }).join('<br/>');
                    } else if (Array.isArray(params)) {
                        // 如果是参数数组
                        return params.map(param => {
                            const fullName = nameMap[param.name] || param.name;
                            return `${fullName}(${param.name}): ${param.value}`;
                        }).join('<br/>');
                    } else {
                        // 单个数据点
                        const fullName = nameMap[params.name] || params.name;
                        return `${fullName}(${params.name}): ${params.value}`;
                    }
                }
            },
            legend: {
                show: false
            },
            radar: {
                indicator: indicators,
                center: mobileFacilityScore ? ['50%', '52%'] : (showTitle ? ['50%', '60%'] : ['50%', '50%']),
                radius: mobileFacilityScore ? '46%' : (showTitle ? '56%' : '70%'),
                axisName: {
                    color: '#666',
                    fontSize: mobileFacilityScore ? 10 : 12,
                    formatter: function(value) {
                        // 直接返回缩写，因为已经转换过了
                        return value;
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                },
                axisLine: {
                    lineStyle: {
                        color: '#e6e6e6'
                    }
                }
            },
            series: [{
                type: 'radar',
                data: radarData.map((item, index) => ({
                    value: item.radarData,
                    name: item.facilityName,
                    itemStyle: {
                        // 使用 facility 的颜色，如果没有则使用默认颜色
                        color: facilityColorMap[item.facilityId] || `hsl(${index * 360 / radarData.length}, 70%, 50%)`
                    },
                    lineStyle: {
                        color: facilityColorMap[item.facilityId] || `hsl(${index * 360 / radarData.length}, 70%, 50%)`
                    },
                    areaStyle: {
                        opacity: 0 // 设置为完全透明，不显示填充
                    }
                }))
            }]
        };
        
        myChart.setOption(option);

        if (mobileFacilityScore) {
            myChart.off('click');
            myChart.on('click', params => {
                const matchedItem = radarData.find(item => item.facilityName === params?.name) || radarData[0];
                if (matchedItem) {
                    setSelectedRadarItem(matchedItem);
                    setDrawerVisible(true);
                }
            });
        }
        
        console.log('Radar chart option set successfully');
        
        // 响应式处理
        const resizeHandler = () => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.resize();
            }
        };
        
        window.addEventListener('resize', resizeHandler);
        
        // 强制重绘
        setTimeout(() => {
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.resize();
            }
        }, 100);
        
        // 清理函数
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    };

    useEffect(() => {
        // 使用 requestAnimationFrame 确保DOM完全渲染
        let cleanupRadarChart;
        let cancelled = false;
        const timer = setTimeout(() => {
            requestAnimationFrame(() => {
                if (!cancelled) {
                    cleanupRadarChart = initRadarChart();
                }
            });
        }, 100);

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (typeof cleanupRadarChart === 'function') {
                cleanupRadarChart();
            }
            if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
                chartInstanceRef.current.dispose();
            }
        };
    }, [radarData, facilities, title, overallScore, showOverallScore, showTitle, useMobileDrawerTooltip]);

    return (
        <>
            <div 
                ref={chartRef}
                style={style}
            />
            {useMobileDrawerTooltip ? (
                <Popup
                    visible={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    position="bottom"
                    closeOnMaskClick
                    closeOnSwipe
                    style={{ "--z-index": 1500 }}
                    bodyStyle={{
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        padding: '16px',
                    }}
                >
                    <div style={{ width: '100%' }}>
                        <div
                            style={{
                                width: 36,
                                height: 4,
                                borderRadius: 999,
                                background: '#d9d9d9',
                                margin: '0 auto 14px',
                            }}
                        />
                        <div style={{ color: '#396598', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                            {drawerRadarItem?.facilityName || title}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {drawerMetrics.map(metric => (
                                <div
                                    key={metric.name}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        padding: '10px 0',
                                        borderBottom: '1px solid #f0f0f0',
                                    }}
                                >
                                    <div>
                                        <div style={{ color: '#111827', fontSize: 14, fontWeight: 500 }}>
                                            {metric.fullName}
                                        </div>
                                        <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                                            {metric.name}
                                        </div>
                                    </div>
                                    <div style={{ color: '#111827', fontSize: 16, fontWeight: 700 }}>
                                        {metric.value ?? '-'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Popup>
            ) : null}
        </>
    );
};

export default SpiderChart;

